import { useState } from 'react'
import { PageHead } from '@components/PageHead'
import { verifyMasterPasswordMatchesSession } from '@features/auth/services/verifyMasterPassword'
import { ToolsSubNav } from '@features/tools/components/ToolsSubNav'
import { runExport, ExportError, type ExportFormat } from '@features/tools/services/exportFlow'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * エクスポート画面 (F3 / B5、Phase 5-6)。
 *
 * json / csv / zip の 3 形式に対応。実行前に confirm modal を表示し、
 * **平文エクスポートの危険性** を明示してから download を発火する。
 * password-encrypted export は別 batch で追加予定。
 */

type Phase = 'idle' | 'confirm' | 'exporting' | 'done' | 'error'

const EXPORT_FORMATS: readonly ExportFormat[] = ['json', 'csv', 'zip']

export const ExportPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const [phase, setPhase] = useState<Phase>('idle')
  const [format, setFormat] = useState<ExportFormat>('json')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  // 平文エクスポート前の本人確認用マスターパスワード。
  const [password, setPassword] = useState('')
  const [confirmError, setConfirmError] = useState<string | null>(null)

  const closeConfirm = (): void => {
    setPhase('idle')
    setPassword('')
    setConfirmError(null)
  }

  const onConfirm = async (): Promise<void> => {
    if (!password) {
      setConfirmError('tools.export.error.wrong_password')

      return
    }
    setPhase('exporting')
    setConfirmError(null)
    setErrorMsg(null)

    // 平文の保管庫を書き出す前に、マスターパスワードで本人確認する。
    const ok = await verifyMasterPasswordMatchesSession(password)
    if (!ok) {
      setConfirmError('tools.export.error.wrong_password')
      setPhase('confirm')

      return
    }

    try {
      await runExport(format)
      setPassword('')
      setPhase('done')
      showToast({ titleKey: 'tools.export.success', tone: 'success', dedupe: true })
    } catch (e) {
      const messageKey = e instanceof ExportError ? e.messageKey : 'tools.export.error.generic'

      setErrorMsg(messageKey)
      setPhase('error')
    }
  }

  return (
    <div>
      <PageHead
        title={t('tools.export.title')}
        sub={t('tools.export.sub')}
        beforeTabs={<ToolsSubNav active="tools/export" />}
      />
      <div className="page-body">
        <Notice tone="warn" ico="alert">
          {t('tools.export.warning')}
        </Notice>

        <div className="card gen-form-card">
          <div className="field">
            <label className="field-label" htmlFor="export-format">
              {t('tools.export.format_label')}
            </label>
            <select
              id="export-format"
              className="select lg"
              value={format}
              onChange={ev => setFormat(ev.target.value as ExportFormat)}
              disabled={phase === 'exporting'}
              data-testid="export-format-select"
            >
              {EXPORT_FORMATS.map(f => (
                <option key={f} value={f}>
                  {t(`tools.export.format_${f}`)}
                </option>
              ))}
            </select>
            <div className="field-help">{t('tools.export.format_hint')}</div>
          </div>

          {errorMsg !== null && (
            <Notice tone="danger" ico="alert">
              {t(errorMsg)}
            </Notice>
          )}

          {phase === 'done' && (
            <Notice tone="info" ico="check">
              {t('tools.export.success')}
            </Notice>
          )}

          <div className="gen-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setPhase('confirm')}
              disabled={phase === 'exporting'}
              data-testid="export-trigger"
            >
              <Icon name="download" aria-hidden="true" />
              {t('tools.export.trigger')}
            </button>
          </div>
        </div>
      </div>

      {(phase === 'confirm' || phase === 'exporting') && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-confirm-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="export-confirm-title">{t('tools.export.confirm_title')}</h3>
            <p>{t('tools.export.confirm_body')}</p>
            <Notice tone="warn" ico="alert">
              {t('tools.export.confirm_warning')}
            </Notice>
            <div className="field">
              <label className="field-label" htmlFor="export-confirm-password">
                {t('tools.export.confirm_password_label')}
              </label>
              <PasswordInput
                id="export-confirm-password"
                value={password}
                onChange={ev => setPassword(ev.target.value)}
                onKeyDown={ev => {
                  if (ev.key === 'Enter') void onConfirm()
                }}
                autoComplete="current-password"
                disabled={phase === 'exporting'}
                data-testid="export-confirm-password"
                autoFocus
              />
              <div className="field-help">{t('tools.export.confirm_password_hint')}</div>
            </div>
            {confirmError !== null && (
              <Notice tone="danger" ico="alert">
                {t(confirmError)}
              </Notice>
            )}
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeConfirm}
                disabled={phase === 'exporting'}
              >
                {t('tools.export.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void onConfirm()}
                disabled={phase === 'exporting' || !password}
                data-testid="export-confirm"
              >
                <Icon name="download" aria-hidden="true" />
                {phase === 'exporting'
                  ? t('tools.export.verifying')
                  : t('tools.export.confirm_run')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
